<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { requestUserListRestore } from "./securityUserListState";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecurityUserAvatar from "@/components/security/SecurityUserAvatar.vue";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import EmployeePermissionMatrix from "@/components/security/EmployeePermissionMatrix.vue";
import EmployeeService from "@/api/services/employee.service";
import EmployeePermissionService from "@/api/services/employeePermission.service";
import UsersExtranetService from "@/api/services/usersExtranet";
import ExtranetUserPermissionService from "@/api/services/extranetUserPermission.service";
import PermissionProfileService from "@/api/services/permissionProfile.service";
import EmployeeRoleService from "@/api/services/employeeRole.service";
import Notification from "@/utils/plugins/NotificationService";
import { useAuthStore } from "@/app/stores/authStore";
import { hasRoleSuperAdmin } from "@/utils/securityAccess";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const isExternal = computed(() => route.query.type === "external");
const goBackToUsers = () => {
  const type = isExternal.value ? "external" : "internal";
  requestUserListRestore(type);
  router.push({ name: `security-${type}-users` });
};
const employeeId = computed(() => route.query.user);
const isSelfTarget = computed(() => !isExternal.value && String(authStore.getUserLogged?.id) === String(employeeId.value));
const actorIsSuperAdmin = hasRoleSuperAdmin(authStore.getUserLogged?.roles);
// Servicio de permisos activo según el tipo de cuenta (Employee vs ExtranetUser) —
// ambos exponen la misma forma getPermissions/updatePermissions (ver Fases 6/7).
const permissionService = computed(() => (isExternal.value ? ExtranetUserPermissionService : EmployeePermissionService));

const activeTab = ref("permissions");
const employee = ref(null);
const extranetUser = ref(null);
const catalog = ref([]);
const permissions = ref({});
const initialPermissions = ref({});
const saving = ref(false);
const profiles = ref([]);
const selectedProfileId = ref(null);
const applyingProfile = ref(false);
const systemRole = ref(null);
const selectedSystemRole = ref(null);
const savingRole = ref(false);

const internalRoleOptions = computed(() => {
  const options = [
    { value: "ROLE_USER", label: "Usuario" },
    { value: "ROLE_SYSTEMS", label: "Sistemas" },
  ];
  if (actorIsSuperAdmin || systemRole.value === "ROLE_ADMIN") {
    options.push({ value: "ROLE_ADMIN", label: "Administrador" });
  }
  return options;
});
const externalRoleOptions = [
  { value: "ROLE_CUSTOMER", label: "Cliente" },
  { value: "ROLE_CUSTOMER_ADMIN", label: "Administrador de cliente" },
];
const roleOptions = computed(() => (isExternal.value ? externalRoleOptions : internalRoleOptions.value));

// El rol solo se puede tocar si: no es uno mismo, no es ya Super Admin (nunca
// gestionable desde el panel), y si toca el nivel Administrador, el actor
// tiene que ser Super Admin (mismas reglas que valida el backend).
const canEditRole = computed(() => {
  if (isExternal.value) return true;
  if (isSelfTarget.value) return false;
  if (systemRole.value === "ROLE_SUPER_ADMIN") return false;
  if (systemRole.value === "ROLE_ADMIN" && !actorIsSuperAdmin) return false;
  return true;
});

const employeeTypeLabels = { employee: "Empleado", intern: "Becario", external_collaborator: "Colaborador externo" };

const initialsOf = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const user = computed(() => {
  if (isExternal.value) {
    if (!extranetUser.value) return { name: "", email: "", department: "", role: "", employeeType: "", lastLogin: "", initials: "", active: true };
    const fullName = `${extranetUser.value.name ?? ""} ${extranetUser.value.lastName ?? ""}`.trim();
    return {
      photo: extranetUser.value.photo,
      name: fullName,
      email: extranetUser.value.email,
      department: "—",
      role: (extranetUser.value.roles ?? []).includes("ROLE_CUSTOMER_ADMIN") ? "Administrador" : "Cliente",
      employeeType: "Usuario externo",
      lastLogin: "—",
      initials: initialsOf(fullName),
      active: extranetUser.value.active,
    };
  }
  if (!employee.value) return { name: "", email: "", department: "", role: "", employeeType: "", lastLogin: "", initials: "", active: true };
  return {
    photo: employee.value.photo,
    name: employee.value.fullName,
    email: employee.value.email,
    department: employee.value.job?.department?.name ?? "—",
    role: employee.value.job?.name ?? "—",
    employeeType: employeeTypeLabels[employee.value.employeeType] ?? employee.value.employeeType,
    lastLogin: "—",
    initials: initialsOf(employee.value.fullName),
    active: employee.value.active,
  };
});

const isDirty = computed(() => Object.keys(permissions.value).some((key) => permissions.value[key] !== initialPermissions.value[key]));

const loadEmployee = () => {
  if (!employeeId.value) return;
  if (isExternal.value) {
    UsersExtranetService.getUser(employeeId.value).then((res) => {
      if (!res) return;
      extranetUser.value = res.data;
      const roles = res.data.roles ?? [];
      systemRole.value = roles.includes("ROLE_CUSTOMER_ADMIN") ? "ROLE_CUSTOMER_ADMIN" : "ROLE_CUSTOMER";
      selectedSystemRole.value = systemRole.value;
    });
    return;
  }
  EmployeeService.getEmployee(employeeId.value).then((res) => {
    employee.value = res.data;
    loadProfiles();
    loadRole();
  });
};

const loadRole = () => {
  EmployeeRoleService.getRole(employeeId.value).then((res) => {
    if (!res) return;
    systemRole.value = res.data.role;
    selectedSystemRole.value = res.data.role;
  });
};

const saveRole = () => {
  if (!selectedSystemRole.value || selectedSystemRole.value === systemRole.value) {
    Notification.info("No hay cambios de rol que guardar.", 3000);
    return;
  }

  savingRole.value = true;
  const request = isExternal.value
    ? UsersExtranetService.updateUser(employeeId.value, {
      roles: selectedSystemRole.value === "ROLE_CUSTOMER_ADMIN" ? ["ROLE_CUSTOMER_ADMIN"] : [],
    })
    : EmployeeRoleService.updateRole(employeeId.value, selectedSystemRole.value);

  request
    .then((res) => {
      if (!res) return;
      systemRole.value = selectedSystemRole.value;
      Notification.success("Rol actualizado correctamente. El cambio se aplicará cuando la persona cierre sesión y vuelva a entrar.", 5000);
    })
    .finally(() => { savingRole.value = false; });
};

const loadCatalog = () => {
  EmployeePermissionService.getCatalogActions().then((res) => {
    catalog.value = res.data ?? [];
  });
};

const applyMatrix = (rows) => {
  const map = {};
  (rows ?? []).forEach((row) => { map[`${row.module}:${row.action}`] = !!row.granted; });
  permissions.value = map;
  initialPermissions.value = { ...map };
};

const loadPermissions = () => {
  if (!employeeId.value) return;
  permissionService.value.getPermissions(employeeId.value).then((res) => {
    if (!res) return;
    applyMatrix(res.data);
  });
};

const toggle = (module, action) => {
  const key = `${module}:${action}`;
  permissions.value = { ...permissions.value, [key]: !permissions.value[key] };
};

const loadProfiles = () => {
  if (isExternal.value) return;
  PermissionProfileService.getProfiles().then((res) => {
    if (!res) return;
    const employeeType = employee.value?.employeeType;
    profiles.value = (res.data ?? []).filter(
      (profile) => !profile.appliesToEmployeeType || profile.appliesToEmployeeType === employeeType,
    );
  });
};

const applyProfile = () => {
  if (!selectedProfileId.value) {
    Notification.warning("Selecciona una plantilla para aplicar.", 3000);
    return;
  }

  applyingProfile.value = true;
  PermissionProfileService.applyToEmployee(employeeId.value, selectedProfileId.value)
    .then((res) => {
      if (!res) return;
      loadPermissions();
      if (res.data.skipped?.length) {
        Notification.warning(
          `Plantilla aplicada. Se omitieron ${res.data.skipped.length} permiso(s) no aptos para este empleado: ${res.data.skipped.join(", ")}.`,
          6000,
        );
      } else {
        Notification.success("Plantilla aplicada correctamente.", 3000);
      }
      selectedProfileId.value = null;
    })
    .finally(() => { applyingProfile.value = false; });
};

const save = () => {
  if (!isDirty.value) {
    Notification.info("No hay cambios que guardar.", 3000);
    return;
  }
  const changes = Object.keys(permissions.value)
    .filter((key) => permissions.value[key] !== initialPermissions.value[key])
    .map((key) => {
      const [module, action] = key.split(":");
      return { module, action, granted: permissions.value[key] };
    });

  saving.value = true;
  permissionService.value.updatePermissions(employeeId.value, changes)
    .then((res) => {
      if (!res) return;
      applyMatrix(res.data);
      Notification.success("Permisos actualizados correctamente.", 3000);
    })
    .finally(() => { saving.value = false; });
};

// Reune todo lo que depende del empleado/usuario externo objetivo (employeeId +
// isExternal, derivados de route.query). Necesario porque "security-user-permissions"
// es una única ruta: al navegar de una ficha a otra desde el listado (InternalUsers.vue/
// ExternalUsers.vue), Vue Router reutiliza el mismo componente sin desmontarlo —
// sin este reset+recarga explícitos, la ficha se quedaba mostrando los datos del
// empleado anterior hasta forzar una recarga completa de página.
const loadTarget = () => {
  employee.value = null;
  extranetUser.value = null;
  systemRole.value = null;
  selectedSystemRole.value = null;
  profiles.value = [];
  selectedProfileId.value = null;
  permissions.value = {};
  initialPermissions.value = {};

  loadEmployee();
  loadPermissions();
};

watch(() => [employeeId.value, isExternal.value], loadTarget);

onMounted(() => {
  loadCatalog();
  loadTarget();
});
</script>

<template>
  <section class="user-permissions">
    <HeaderTitle title="Seguridad" />
    <div class="row align-items-center security-header">
      <div class="col-12 col-md-2 text-start">
        <button type="button" class="return-btn btn btn-grey security-back-button" @click="goBackToUsers">
          <font-awesome-icon icon="angle-left" /> Volver
        </button>
      </div>
      <div class="col-12 col-md-8">
        <SecuritySectionTitle title="Usuario · Permisos" />
      </div>
    </div>
    <el-card class="security-user-card">
      <SecurityUserAvatar class="security-user-card__avatar" :photo="user.photo" :initials="user.initials" />
      <div class="security-user-card__identity"><b>{{ user.name }}</b><small>{{ user.email }}</small><span
          class="status" :class="user.active ? 'active' : 'inactive'">{{ user.active ? 'Activo' : 'Inactivo' }}</span>
      </div>
      <dl>
        <div>
          <dt>Departamento</dt>
          <dd>{{ user.department }}</dd>
        </div>
        <div>
          <dt>Puesto/Rol</dt>
          <dd>{{ user.role }}</dd>
        </div>
        <div>
          <dt>Tipo empleado</dt>
          <dd>{{ user.employeeType }}</dd>
        </div>
        <div>
          <dt>Último login</dt>
          <dd>{{ user.lastLogin }}</dd>
        </div>
      </dl>
    </el-card>
    <el-card class="security-role-card">
      <h3 class="security-role-card__title">Rol de acceso</h3>
      <p v-if="isSelfTarget" class="security-role-card__note">No puedes cambiar tu propio rol.</p>
      <p v-else-if="systemRole === 'ROLE_SUPER_ADMIN'" class="security-role-card__note">
        El rol de Super Admin no se gestiona desde el panel.
      </p>
      <p v-else-if="!canEditRole" class="security-role-card__note">
        Este usuario tiene rol Administrador — solo un Super Admin puede cambiarlo.
      </p>
      <div v-else class="security-role-card__controls">
        <el-select v-model="selectedSystemRole" class="security-role-card__select">
          <el-option v-for="opt in roleOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
        <button class="btn btn_yellow security-role-card__button" :disabled="savingRole" @click="saveRole">
          GUARDAR ROL
        </button>
      </div>
    </el-card>
    <el-card class="security-permissions-card"><el-tabs v-model="activeTab" class="security-tabs">
        <!--
        <el-tab-pane label="INFORMACIÓN GENERAL" name="information" />
        -->
        <el-tab-pane label="PERMISOS" name="permissions" />
        <!--
        <el-tab-pane label="ACCESOS A CLIENTES" name="clients" />
        <el-tab-pane label="HISTORIAL" name="history" />
        -->
      </el-tabs><template v-if="activeTab === 'permissions'">
        <p class="security-notice"><font-awesome-icon icon="exclamation-circle" /> Al cambiar permisos sensibles,
          asegúrate de que el usuario realmente los necesita.</p>
        <div v-if="!isExternal" class="apply-profile">
          <el-select v-model="selectedProfileId" class="apply-profile__select" placeholder="Aplicar una plantilla de permisos"
            clearable filterable>
            <el-option v-for="profile in profiles" :key="profile.id" :label="profile.name" :value="profile.id" />
          </el-select>
          <button class="btn btn_yellow apply-profile__button" :disabled="applyingProfile || !selectedProfileId"
            @click="applyProfile">APLICAR PLANTILLA</button>
        </div>
        <EmployeePermissionMatrix :catalog="catalog" :permissions="permissions" @toggle="toggle" />
      </template>
      <p v-else class="security-empty-tab">Esta sección estará disponible próximamente.</p>
    </el-card>
    <div v-if="activeTab === 'permissions'" class="security-actions">
      <button type="button" class="btn btn_red security-save-button"
        :disabled="saving" @click="save">GUARDAR</button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.user-permissions {
  color: #30385d;
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 12px;
}

.security-subtitle .svg-inline--fa {
  color: #474ce6;
  font-size: 27px;
}

.security-subtitle .title-text {
  color: #474ce6;
  font-size: 2.5em;
  margin-left: 1rem;
}

.security-user-card {
  margin-bottom: 18px;
}

.security-user-card :deep(.el-card__body) {
  align-items: center;
  display: flex;
  gap: 24px;
  padding: 18px 24px;
}

.security-user-card__avatar {
  align-items: center;
  background: #96634d;
  border: 3px solid #d8dcff;
  border-radius: 50%;
  color: #fff;
  display: flex;
  flex: 0 0 92px;
  font-size: 1rem;
  font-weight: 800;
  height: 92px;
  justify-content: center;
  width: 92px;
}

.security-user-card__identity {
  display: grid;
  gap: 6px;
  min-width: 190px;
}

.security-user-card__identity b {
  font-size: 1.05rem;
}

.security-user-card__identity small {
  font-size: .85rem;
}

.security-user-card__identity .status {
  justify-self: start;
  min-width: 112px;
  text-align: center;
}

.security-user-card dl {
  display: grid;
  gap: 8px 34px;
  grid-template-columns: repeat(4, minmax(115px, max-content));
  margin: 0;
}

.security-user-card dt {
  color: #7c83a2;
  font-size: .82rem;
  font-weight: 700;
}

.security-user-card dd {
  font-size: .95rem;
  font-weight: 800;
  margin: 4px 0 0;
}

.status {
  border: 1px solid;
  border-radius: 15px;
  font-size: .75rem;
  padding: 3px 10px;
}

.active {
  background: #effbf4;
  border-color: #c9eed9;
  color: #279653;
}

.inactive {
  background: #fff7ef;
  border-color: #ffe0ba;
  color: #fb6b21;
}

.security-permissions-card {
  margin-bottom: 18px;
}

.security-role-card {
  margin-bottom: 18px;
}

.security-role-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.security-role-card__title {
  color: #474ce6;
  font-size: 1rem;
  font-weight: 800;
  margin: 0 0 12px;
}

.security-role-card__note {
  color: #7c83a2;
  font-size: .85rem;
  margin: 0;
}

.security-role-card__controls {
  align-items: center;
  display: flex;
  gap: 12px;
}

.security-role-card__select {
  max-width: 260px;
  width: 100%;
}

.security-role-card__button {
  color: #474ce6 !important;
  flex: 0 0 auto;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 16px !important;
  width: auto !important;
}

@media(max-width:767px) {
  .security-role-card__controls {
    flex-direction: column;
    align-items: stretch;
  }

  .security-role-card__select {
    max-width: 100%;
  }
}

.security-tabs :deep(.el-tabs__item) {
  color: #626a91;
  font-size: .72rem;
  font-weight: 800;
}

.security-tabs :deep(.el-tabs__item.is-active) {
  color: #474ce6;
}

.security-notice {
  background: #fff5d8;
  border: 1px solid #f7d20d;
  border-radius: 8px;
  color: #715e22;
  font-size: .78rem;
  margin: 4px 0 14px;
  padding: 8px 10px;
}

.security-notice .svg-inline--fa {
  color: #f7c800;
  margin-right: 5px;
}

.apply-profile {
  align-items: center;
  display: flex;
  gap: 12px;
  margin: 0 0 16px;
}

.apply-profile__select {
  max-width: 320px;
  width: 100%;
}

.apply-profile__button {
  color: #474ce6 !important;
  flex: 0 0 auto;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 16px !important;
  width: auto !important;
}

@media(max-width:767px) {
  .apply-profile {
    flex-direction: column;
    align-items: stretch;
  }

  .apply-profile__select {
    max-width: 100%;
  }
}

.security-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  margin: 0 0 20px;
}

.security-header {
  margin-bottom: 12px;
}

.security-header :deep(.security-section-title) {
  margin-bottom: 0;
}

.security-back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0;
  padding-left: 0;
}

.security-save-button {
  font-size: 14px !important;
  height: 3em !important;
  min-height: 3em !important;
  padding: 0 18px !important;
  width: 180px !important;
}

@media(max-width:767px) {
  .security-subtitle .title-text {
    font-size: 2em;
  }

  .security-user-card :deep(.el-card__body) {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 16px;
  }

  .security-user-card__avatar {
    flex-basis: 72px;
    height: 72px;
    width: 72px;
  }

  .security-user-card dl {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .security-tabs :deep(.el-tabs__nav) {
    white-space: nowrap;
  }

  .security-tabs :deep(.el-tabs__header) {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }
}
</style>
